import { useState, type MouseEvent, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ExternalLink, FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { useQuoteBuilderApi } from '../api/client'
import type { QuoteLine } from '../api/types'
import { PageState } from '../ui/page'
import { EditorModalContent } from '../editor/modal'
import { ProductAlternatives } from './alternatives'
import { ManufacturerLink, ProductLink } from './links'
import { ProductImage } from './product-image'
import { assetDownloadQueryOptions, productQueryOptions, type CatalogAsset, type ProductDetail } from './queries'

type OpenDetails = (event: MouseEvent<HTMLButtonElement>) => void

const dimensionLabels: Record<string, string> = {
  width: 'Width',
  depth: 'Depth',
  height: 'Height',
  weight: 'Weight',
  length: 'Length',
}

/**
 * Product details in place (demo `ProductDetailsModal`). `children` receives the opener, or
 * undefined for lines without a catalog product. Root equipment lines also show their
 * same-category alternatives.
 */
export function ProductDetailsModal({
  productId,
  model,
  quoteId,
  line,
  children,
}: {
  productId: number | null | undefined
  model: string
  quoteId?: string
  line?: QuoteLine
  children: (onDetails?: OpenDetails) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null)
  return (
    <Dialog onOpenChange={setOpen} open={open}>
      {children(
        productId
          ? (event) => {
              setTrigger(event.currentTarget)
              setOpen(true)
            }
          : undefined,
      )}
      {open && productId ? (
        <EditorModalContent
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            trigger?.focus()
          }}
          size="lg"
          title={`Product details${model ? ` / ${model}` : ''}`}
        >
          <ProductDetails key={productId} productId={productId} />
          {quoteId && line?.product_id === productId && line.line_type === 'product' && !line.parent_line_id ? (
            <ProductAlternatives key={line.id} line={line} quoteId={quoteId} />
          ) : null}
        </EditorModalContent>
      ) : null}
    </Dialog>
  )
}

function ProductDetails({ productId }: { productId: number }) {
  const api = useQuoteBuilderApi()
  const query = useQuery({
    ...productQueryOptions(api, productId),
    retry: false,
  })
  if (query.isPending) return <PageState compact kind="loading" title="Loading product details" />
  if (!query.data)
    return (
      <PageState
        action={
          <Button onClick={() => void query.refetch()} size="sm" variant="outline">
            Try again
          </Button>
        }
        compact
        description="If the product has left the catalog, its details are no longer available."
        kind="error"
        title="Product details could not be loaded"
      />
    )
  return <ProductDetailContent product={query.data} />
}

export function ProductDetailContent({ product }: { product: ProductDetail }) {
  const documents = product.assets.filter(
    (asset) => asset.asset_type !== 'image' && !asset.mime_type?.startsWith('image/'),
  )
  const specifications = product.specifications.map((spec): [string, string] => [
    spec.label,
    spec.unit && !spec.value.toLowerCase().endsWith(spec.unit.toLowerCase())
      ? `${spec.value} ${spec.unit}`
      : spec.value,
  ])
  const dimensions = Object.entries(product.dimensions ?? {}).map(([key, value]): [string, string] => [
    dimensionLabels[key] ?? key.replaceAll('_', ' '),
    value,
  ])
  const attributes = (
    [
      ['Selling unit', product.selling_unit],
      ['Shipping weight', product.shipping_weight ? `${Number(product.shipping_weight)} lb` : null],
      ['Freight class', product.freight_class],
      ['Ships from ZIP', product.ship_from_zip],
      ['Stock model', product.stock_model],
    ] as Array<[string, string | null]>
  ).filter((entry): entry is [string, string] => Boolean(entry[1]))
  const paragraphs = [product.description].filter((text): text is string => Boolean(text?.trim()))
  const path = (product.taxonomy.path ?? []).map((node) => node.label).join(' / ')

  return (
    <div className="space-y-6">
      <div className="grid items-start gap-5 sm:grid-cols-[180px_minmax(0,1fr)]">
        <ProductImage
          alt={`${product.manufacturer} ${product.model}`}
          className="mx-auto h-36 w-44 rounded-md sm:h-44"
          url={product.primary_asset?.url}
        />
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">
            <ManufacturerLink uuid={product.manufacturer_uuid}>{product.manufacturer}</ManufacturerLink>
          </p>
          <h2 className="mt-1 break-words text-xl font-bold leading-7">
            <ProductLink uuid={product.uuid}>{product.model}</ProductLink>
          </h2>
          <p className="mt-1 break-words text-sm font-medium leading-5">{product.title}</p>
          {product.subtitle ? <p className="mt-1 text-sm text-muted-foreground">{product.subtitle}</p> : null}
          {path ? <p className="mt-2 text-xs text-muted-foreground">{path}</p> : null}
          {product.gtin || product.country_of_origin ? (
            <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
              {product.gtin ? (
                <div>
                  <dt className="text-xs text-muted-foreground">GTIN</dt>
                  <dd className="tabular-nums">{product.gtin}</dd>
                </div>
              ) : null}
              {product.country_of_origin ? (
                <div>
                  <dt className="text-xs text-muted-foreground">Country of origin</dt>
                  <dd>{product.country_of_origin}</dd>
                </div>
              ) : null}
            </dl>
          ) : null}
        </div>
      </div>
      <section className="border-y py-2">
        <h3 className="text-sm font-semibold">Documents</h3>
        {documents.length ? (
          <div className="divide-y">
            {documents.map((asset) => (
              <ProductDocument asset={asset} key={asset.id} />
            ))}
          </div>
        ) : (
          <p className="py-2 text-sm text-muted-foreground">No documents available.</p>
        )}
      </section>
      <section>
        <h3 className="mb-2 text-sm font-semibold">Description</h3>
        {paragraphs.length || product.overview.length ? (
          <>
            {paragraphs.map((paragraph) => (
              <p
                className="mt-2 whitespace-pre-line break-words text-sm leading-6 text-muted-foreground"
                key={paragraph}
              >
                {paragraph}
              </p>
            ))}
            {product.overview.length ? (
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-6 text-muted-foreground">
                {product.overview.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">No description available.</p>
        )}
      </section>
      {product.features.length ? (
        <section>
          <h3 className="mb-2 text-sm font-semibold">Features</h3>
          <ul className="list-disc space-y-1.5 pl-5 text-sm leading-6 text-muted-foreground">
            {product.features.map((feature) => (
              <li className="break-words" key={feature.name}>
                <span className="font-medium text-foreground">{feature.name}</span>
                {feature.description ? `: ${feature.description}` : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <div className="grid items-start gap-6 sm:grid-cols-2">
        <FactSection entries={specifications} title="Specifications" />
        <div className="space-y-5">
          {dimensions.length ? <FactSection entries={dimensions} title="Dimensions" /> : null}
          {attributes.length ? <FactSection entries={attributes} title="Shipping and ordering" /> : null}
        </div>
      </div>
    </div>
  )
}

function FactSection({ entries, title }: { entries: Array<[string, string]>; title: string }) {
  return (
    <section className="border-t pt-3">
      <h3 className="mb-2 text-sm font-semibold">{title}</h3>
      {entries.length ? (
        <dl className="divide-y">
          {entries.map(([label, value], index) => (
            <div className="grid grid-cols-2 gap-3 py-2 text-sm leading-5" key={`${label}-${index}`}>
              <dt className="break-words text-muted-foreground">{label}</dt>
              <dd className="break-words font-medium">{value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="text-sm text-muted-foreground">No specifications available.</p>
      )}
    </section>
  )
}

function ProductDocument({ asset }: { asset: CatalogAsset }) {
  const api = useQuoteBuilderApi()
  const download = useQuery({
    ...assetDownloadQueryOptions(api, asset.id),
    enabled: false,
  })
  const name = asset.name.trim() || 'Product document'
  const open = async () => {
    // Open synchronously for popup protection, then point the tab at a fresh signed link.
    const tab = window.open('about:blank', '_blank')
    if (tab) tab.opener = null
    const result = await download.refetch()
    if (result.isError || !result.data?.url) {
      tab?.close()
      return
    }
    if (tab) tab.location.replace(result.data.url)
  }
  return (
    <div className="py-1.5">
      <div className="flex min-h-10 items-center gap-2">
        <FileText aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 break-words text-sm">{name}</span>
        {asset.url ? (
          <Button
            aria-label={`Open ${name}`}
            onClick={() => window.open(asset.url ?? undefined, '_blank', 'noopener,noreferrer')}
            size="sm"
            variant="ghost"
          >
            Open
            <ExternalLink aria-hidden="true" />
          </Button>
        ) : asset.download_available ? (
          <Button
            aria-label={`Open ${name}`}
            disabled={download.isFetching}
            onClick={() => void open()}
            size="sm"
            variant="ghost"
          >
            {download.isFetching ? 'Opening...' : download.isError ? 'Retry' : 'Open'}
          </Button>
        ) : (
          <span className="text-sm text-muted-foreground">Unavailable</span>
        )}
      </div>
      {download.isError ? (
        <p className="mt-1 text-sm text-destructive" role="alert">
          The document could not be opened. Try again.
        </p>
      ) : null}
    </div>
  )
}
